export default (config = {}) => ({
    showTools:
        config.showTools !== undefined ? config.showTools : false,
    startClosed:
        config.startClosed !== undefined ? config.startClosed : false,
    expandDescendants:
        config.expandDescendants !== undefined ? config.expandDescendants : false,
    collapsed: {},
    init() {
        // add classes to the main element.
        this.$el.classList.add('[&_*]:transition-all', '[&_*]:duration-300');
        // Helper: clone an element and remove any descendant with data-ignore.
        const getCleanHTML = (el) => {
            const clone = el.cloneNode(true);
            clone.querySelectorAll('[data-ignore]').forEach(node => node.remove());
            return clone.outerHTML;
        };
        const getCleanText = (el) => {
            const clone = el.cloneNode(true);
            clone.querySelectorAll('[data-ignore]').forEach(node => node.remove());
            return clone.innerText;
        };
        // NEW: Define getFullHTML to remove inline collapse-styles (from both root and descendants)
        const getFullHTML = (el) => {
            const clone = el.cloneNode(true);
            clone.querySelectorAll('[data-ignore]').forEach(node => node.remove());
            [clone, ...clone.querySelectorAll('*')].forEach(node => {
                if (node.style.maxHeight === "0px") node.style.maxHeight = "";
                if (node.style.overflow === "hidden") node.style.overflow = "";
                if (node.style.opacity === "0") node.style.opacity = "";
            });
            return clone.outerHTML;
        };

        // NEW: Helper to temporarily make visible elements marked with data-makevisibletocopy
        const withVisibleElements = async (callback) => {
            const elems = [...this.$el.querySelectorAll('[data-makevisibletocopy]')];
            elems.forEach(el => {
                el.dataset.precopydisplaystate = el.style.display;
                el.style.display = '';
            });
            try {
                await callback();
            } finally {
                elems.forEach(el => {
                    el.style.display = el.dataset.precopydisplaystate;
                    delete el.dataset.precopydisplaystate;
                });
            }
        };

        // NEW: Feature detect HTML clipboard support—but force text mode on Android.
        const isAndroid = /Android/i.test(navigator.userAgent);
        const htmlCopySupported = !isAndroid && (() => {
            try {
                new ClipboardItem({ 'text/html': new Blob(['<b>test</b>'], { type: 'text/html' }) });
                return true;
            } catch (e) {
                return false;
            }
        })();
        // If HTML copy is unsupported or on Android, force text only.
        if (!htmlCopySupported) {
            this.copyMode = 'text';
        } else {
            this.copyMode = 'html';
        }

        if (this.showTools) {
            // Create a controls container for the "toggle all" and "copy all" buttons.
            const controlsDiv = document.createElement('div');
            controlsDiv.dataset.ignore = 'true';
            controlsDiv.style.display = 'flex';
            controlsDiv.style.justifyContent = 'start';
            controlsDiv.style.gap = '1rem';
            controlsDiv.style.marginBottom = '1rem';
            controlsDiv.className = 'text-2xl';

            // Create the toggle all button.
            this.toggleAllBtn = document.createElement('button');
            this.toggleAllBtn.dataset.ignore = 'true';
            this.toggleAllBtn.setAttribute('title', 'Toggle all');
            this.toggleAllBtn.className = 'h-4 transform';
            this.toggleAllBtn.style.border = 'none';
            this.toggleAllBtn.style.background = 'transparent';
            this.toggleAllBtn.style.cursor = 'pointer';
            this.toggleAllBtn.style.outline = 'none';
            this.toggleAllBtn.innerHTML = `
                <span class="toggle-all-closed relative gap-1 justify-start items-center" style="display: flex;">
                    <svg class="align-baseline inline h-[1em] w-[1em] fill-current text-vvbrown object-center object-cover" stroke-width="1.5" viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg">
                        <rect width="256" height="256" fill="none"/>
                        <line x1="216" y1="128" x2="40" y2="128" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                        <line x1="128" y1="96" x2="128" y2="16" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                        <polyline points="96 48 128 16 160 48" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                        <line x1="128" y1="160" x2="128" y2="240" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                        <polyline points="160 208 128 240 96 208" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                    </svg>
                    <span class="text-xs text-left w-16 font-normal block">expand all</span>
                </span>
                <span class="toggle-all-opened relative gap-1 justify-start items-center" style="display: none;">
                    <svg class="align-baseline inline h-[1em] w-[1em] fill-current text-vvbrown object-center object-cover" stroke-width="1.5" viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg">
                        <rect width="256" height="256" fill="none"/>
                        <line x1="216" y1="128" x2="40" y2="128" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                        <line x1="128" y1="16" x2="128" y2="96" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                        <polyline points="160 64 128 96 96 64" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                        <line x1="128" y1="240" x2="128" y2="160" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                        <polyline points="96 192 128 160 160 192" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                    </svg>
                    <span class="text-xs text-left w-16 font-normal block">close all</span>
                </span>
            `;
            this.toggleAllBtn.addEventListener('click', () => {
                this.toggleAll();
                this.updateToggleAllBtnText();
            });
            controlsDiv.appendChild(this.toggleAllBtn);
            // --- Create copy mode toggle button only if HTML copy is supported ---
            if (htmlCopySupported) {
                this.copyModeBtn = document.createElement('button');
                this.copyModeBtn.dataset.ignore = 'true';
                this.copyModeBtn.setAttribute('title', 'Toggle copy mode: Doc or Markdown text');
                this.copyModeBtn.className = 'h-4 ew-6 mr-4 transform';
                this.copyModeBtn.style.border = 'none';
                this.copyModeBtn.style.background = 'transparent';
                this.copyModeBtn.style.cursor = 'pointer';
                this.copyModeBtn.style.outline = 'none';
                this.copyModeBtn.innerHTML = `
                    <span class="copy-mode-html gap-1 justify-start items-center" style="display: flex;">
                    <svg class="inline h-[1em] w-[1em] fill-current text-vvbrown object-center object-cover" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">
                        <rect width="256" height="256" fill="none"/>
                        <path d="M200,24H72A16,16,0,0,0,56,40V64H40A16,16,0,0,0,24,80v96a16,16,0,0,0,16,16H56v24a16,16,0,0,0,16,16H200a16,16,0,0,0,16-16V40A16,16,0,0,0,200,24ZM68.24,153.94l-12-48a8,8,0,1,1,15.52-3.88l6.76,27,6.32-12.66a8,8,0,0,1,14.32,0l6.32,12.66,6.76-27a8,8,0,0,1,15.52,3.88l-12,48a8,8,0,0,1-6.89,6,8.46,8.46,0,0,1-.87.05,8,8,0,0,1-7.16-4.42L92,137.89l-8.84,17.69a8,8,0,0,1-14.92-1.64ZM200,216H72V192h72a16,16,0,0,0,16-16v-8h40Zm0-64H160V104h40Zm0-64H160V80a16,16,0,0,0-16-16H72V40H200Z"/>
                    </svg>
                    <span class="text-xs text-left w-12 font-normal block">for docs</span>
                    </span>
                    <span class="copy-mode-text gap-1 justify-start items-center" style="display: none;">
                    <svg class="inline h-[1em] w-[1em] fill-current text-vvbrown object-center object-cover" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">
                        <rect width="256" height="256" fill="none"/>
                        <line x1="24" y1="56" x2="40" y2="56" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                        <line x1="120" y1="56" x2="136" y2="56" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                        <line x1="24" y1="136" x2="48" y2="136" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                        <line x1="112" y1="136" x2="136" y2="136" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                        <line x1="168" y1="104" x2="240" y2="104" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                        <line x1="168" y1="136" x2="240" y2="136" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                        <line x1="72" y1="168" x2="240" y2="168" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                        <line x1="72" y1="200" x2="240" y2="200" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                        <polyline points="40 136 40 56 80 120 120 56 120 136" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                    </svg>
                    <span class="text-xs text-left w-12 font-normal block">for text</span>
                    </span>
                `;
                this.copyModeBtn.addEventListener('click', () => {
                    if (this.copyMode === 'html') {
                        this.copyMode = 'text';
                        this.copyModeBtn.querySelector('.copy-mode-html').style.display = 'none';
                        this.copyModeBtn.querySelector('.copy-mode-text').style.display = 'flex';
                    } else {
                        this.copyMode = 'html';
                        this.copyModeBtn.querySelector('.copy-mode-html').style.display = 'flex';
                        this.copyModeBtn.querySelector('.copy-mode-text').style.display = 'none';
                    }
                });
                controlsDiv.appendChild(this.copyModeBtn);
            }
            // --- END NEW ---

            // If copying is enabled, add a copy all button.
            this.copyAllBtn = document.createElement('button');
            this.copyAllBtn.dataset.ignore = 'true';
            this.copyAllBtn.setAttribute('title', 'Copy all to clipboard');
            this.copyAllBtn.className = 'h-4 ew-4 mr-4 transform';
            this.copyAllBtn.style.border = 'none';
            this.copyAllBtn.style.background = 'transparent';
            this.copyAllBtn.style.cursor = 'pointer';
            this.copyAllBtn.style.outline = 'none';
            this.copyAllBtn.innerHTML = `
                <span class="copy-all-not-copied gap-1 relative justify-start items-center" style="display: flex;">
                <svg class="inline h-[1em] w-[1em] fill-current text-vvbrown object-center object-cover" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
                <path d="M336 64h-88.6c.4-2.6.6-5.3.6-8 0-30.9-25.1-56-56-56s-56 25.1-56 56c0 2.7.2 5.4.6 8H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h288c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48zM192 32c13.3 0 24 10.7 24 24s-10.7 24-24 24-24-10.7-24-24 10.7-24 24-24zm160 432c0 8.8-7.2 16-16 16H48c-8.8 0-16-7.2-16-16V112c0-8.8 7.2-16 16-16h48v20c0 6.6 5.4 12 12 12h168c6.6 0 12-5.4 12-12V96h48c8.8 0 16 7.2 16 16z"></path>
                </svg>
                <span class="text-xs text-left w-16 font-normal block">copy all</span>
                    <span class="text-[.30em] leading-none font-bold absolute left-[5%] top-[30%] text-center block" x-text="copyMode == 'html' ? 'doc' : 'text'"></span>
                </span>
                <span class="copy-all-copied gap-1 justify-start items-center" style="display: none;">
                <svg class="inline h-[1em] w-[1em] fill-current text-vvbrown object-center object-cover" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
                <path d="M336 64h-80c0-35.3-28.7-64-64-64s-64 28.7-64 64H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h288c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48zM192 40c13.3 0 24 10.7 24 24s-10.7 24-24 24-24-10.7-24-24 10.7-24 24-24zm121.2 231.8l-143 141.8c-4.7 4.7-12.3 4.6-17-.1l-82.6-83.3c-4.7-4.7-4.6-12.3.1-17L99.1 285c4.7-4.7 12.3-4.6 17 .1l46 46.4 106-105.2c4.7-4.7 12.3-4.6 17 .1l28.2 28.4c4.7 4.8 4.6 12.3-.1 17z"></path>
                </svg>
                <span class="text-xs text-left w-16 font-normal block">copied all</span>
                </span>
            `;
            this.copyAllBtn.addEventListener('click', async event => {
                event.stopPropagation();
                await withVisibleElements(async () => {
                    let htmlToCopy = '';
                    let textToCopy = '';
                    [...this.$el.children].forEach(child => {
                        if (child.dataset.ignore) return;
                        htmlToCopy += (this.copyMode === 'html' && htmlCopySupported ? getFullHTML(child) : getCleanHTML(child));
                        textToCopy += getCleanText(child) + '\n';
                    });
                    if (event.ctrlKey || event.metaKey || this.copyMode === 'text') {
                        if (window.TurndownService) {
                            const turndownService = new TurndownService({ headingStyle: 'atx' });
                            if (window.turndownPluginGfmTables) {
                                turndownService.use(window.turndownPluginGfmTables);
                            }
                            navigator.clipboard.writeText(turndownService.turndown(htmlToCopy).trim());
                        } else {
                            navigator.clipboard.writeText(textToCopy.trim());
                        }
                    } else {
                        const blobInput = new Blob([htmlToCopy], { type: 'text/html' });
                        const clipboardItem = new ClipboardItem({ 'text/html': blobInput });
                        navigator.clipboard.write([clipboardItem]);
                    }
                });
                const notCopied = this.copyAllBtn.querySelector('.copy-all-not-copied');
                const copiedSpan = this.copyAllBtn.querySelector('.copy-all-copied') || this.copyAllBtn.querySelector('.copy-copied');
                if (notCopied && copiedSpan) {
                    notCopied.style.display = 'none';
                    copiedSpan.style.display = 'flex';
                    setTimeout(() => {
                        notCopied.style.display = 'flex';
                        copiedSpan.style.display = 'none';
                    }, 2000);
                }
            });
            controlsDiv.appendChild(this.copyAllBtn);
            this.$el.insertBefore(controlsDiv, this.$el.firstChild);
        }

        let counters = [0, 0, 0, 0, 0, 0];
        let lastHeader = '';
        let topLevel = null;
        [...this.$el.children].forEach(el => {
            if (el.dataset.ignore) return;
            if (/^H[1-6]$/.test(el.tagName)) {
                const lvl = Number(el.tagName[1]);
                if (topLevel === null) topLevel = lvl;
                counters[lvl - 1]++;
                for (let i = lvl; i < counters.length; i++) {
                    counters[i] = 0;
                }
                const headerId = counters.slice(0, lvl).join('.');
                el.dataset.level = headerId;
                lastHeader = headerId;
                // Start with the header collapsed.
                this.collapsed[headerId] = this.startClosed;
                // Ensure header has relative positioning for its copy button.
                el.style.position = 'relative';
                // Add pointer cursor and disable text selection.
                el.classList.add('cursor-pointer', 'select-none', 'flex', 'items-start');
                // Header click toggles collapse.
                el.addEventListener('click', () => {
                    this.collapsed[headerId] = !this.collapsed[headerId];
                    // NEW: If expandDescendants is true and this header is expanded, expand all descendants.
                    if (this.expandDescendants && !this.collapsed[headerId]) {
                        for (let key in this.collapsed) {
                            if (key.startsWith(headerId + '.')) {
                                this.collapsed[key] = false;
                            }
                        }
                    }
                    this.updateVisibility();
                    this.updateToggleAllBtnText();
                });
                if (this.showTools) {
                    const headerToggleBtn = document.createElement('button');
                    headerToggleBtn.className = 'inline-block h-[1.5em] leading-none align-middle fill-current shrink-0';
                    headerToggleBtn.dataset.ignore = 'true';
                    headerToggleBtn.dataset.headerToggle = 'true';
                    headerToggleBtn.style.border = 'none';
                    headerToggleBtn.style.background = 'transparent';
                    headerToggleBtn.style.cursor = 'pointer';
                    headerToggleBtn.style.outline = 'none';
                    headerToggleBtn.innerHTML = `
                    <span class="toggle-header-closed relative" style="display: block;">
                        <svg class="align-baseline inline h-[1em] w-[1em] fill-current text-vvbrown object-center object-cover" stroke-width="1.5" viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg">
                        <rect width="256" height="256" fill="none"/>
                        <line x1="216" y1="128" x2="40" y2="128" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                        <line x1="128" y1="96" x2="128" y2="16" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                        <polyline points="96 48 128 16 160 48" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                        <line x1="128" y1="160" x2="128" y2="240" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                        <polyline points="160 208 128 240 96 208" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                        </svg>
                    </span>
                    <span class="toggle-header-opened relative" style="display: none;">
                        <svg class="align-baseline inline h-[1em] w-[1em] fill-current text-vvbrown object-center object-cover" stroke-width="1.5" viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg">
                            <rect width="256" height="256" fill="none"/>
                            <line x1="216" y1="128" x2="40" y2="128" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                            <line x1="128" y1="16" x2="128" y2="96" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                            <polyline points="160 64 128 96 96 64" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                            <line x1="128" y1="240" x2="128" y2="160" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                            <polyline points="96 192 128 160 160 192" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                        </svg>
                    </span>
                    `;
                    if (!this.collapsed[headerId]) {
                        headerToggleBtn.querySelector('.toggle-header-closed').style.display = 'none';
                        headerToggleBtn.querySelector('.toggle-header-opened').style.display = 'block';
                    } else {
                        headerToggleBtn.querySelector('.toggle-header-closed').style.display = 'block';
                        headerToggleBtn.querySelector('.toggle-header-opened').style.display = 'none';
                    }
                    headerToggleBtn.addEventListener('click', (event) => {
                        event.stopPropagation();
                        this.collapsed[headerId] = !this.collapsed[headerId];
                        // NEW: If expandDescendants is true and this header is expanded, expand all descendants.
                        if (this.expandDescendants && !this.collapsed[headerId]) {
                            for (let key in this.collapsed) {
                                if (key.startsWith(headerId + '.')) {
                                    this.collapsed[key] = false;
                                }
                            }
                        }
                        this.updateVisibility();
                        this.updateToggleAllBtnText();
                    });
                    el.insertBefore(headerToggleBtn, el.firstChild);
                }
                // Output the copy button only for headers at the top level.
                if (this.showTools && lvl === topLevel) {
                    const headerEl = el;
                    const copyBtn = document.createElement('button');
                    copyBtn.dataset.ignore = 'true';
                    copyBtn.setAttribute('title', 'Copy to clipboard');
                    copyBtn.className = 'transform ml-auto shrink-0 flex justify-end';
                    copyBtn.style.border = 'none';
                    copyBtn.style.background = 'transparent';
                    copyBtn.style.cursor = 'pointer';
                    copyBtn.style.outline = 'none';
                    copyBtn.innerHTML = `
                        <span class="copy-not-copied gap-.5 justify-start items-center" style="display: flex;">
                            <svg class="inline h-[1em] w-[1em] fill-current text-vvbrown object-center object-cover" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
                                <path d="M336 64h-88.6c.4-2.6.6-5.3.6-8 0-30.9-25.1-56-56-56s-56 25.1-56 56c0 2.7.2 5.4.6 8H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h288c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48zM192 32c13.3 0 24 10.7 24 24s-10.7 24-24 24-24-10.7-24-24 10.7-24 24-24zm160 432c0 8.8-7.2 16-16 16H48c-8.8 0-16-7.2-16-16V112c0-8.8 7.2-16 16-16h48v20c0 6.6 5.4 12 12 12h168c6.6 0 12-5.4 12-12V96h48c8.8 0 16 7.2 16 16z"></path>
                            </svg>
                        <span class=" w-8 text-xs font-normal block">&nbsp;copy&nbsp;</span>
                            <span class="text-[.25em] font-bold absolute left-[12%] top-[28%] text-center block" x-text="copyMode == 'html' ? 'doc' : 'text'"></span>
                        </span>
                        <span class="copy-copied gap-.5 justify-start items-center" style="display: none;">
                            <svg class="inline h-[1em] w-[1em] fill-current text-vvbrown object-center object-cover" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
                                <path d="M336 64h-80c0-35.3-28.7-64-64-64s-64 28.7-64 64H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h288c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48zM192 40c13.3 0 24 10.7 24 24s-10.7 24-24 24-24-10.7-24-24 10.7-24 24-24zm121.2 231.8l-143 141.8c-4.7 4.7-12.3 4.6-17-.1l-82.6-83.3c-4.7-4.7-4.6-12.3.1-17L99.1 285c4.7-4.7 12.3-4.6 17 .1l46 46.4 106-105.2c4.7-4.7 12.3-4.6 17 .1l28.2 28.4c4.7 4.8 4.6 12.3-.1 17z"></path>
                            </svg>
                        <span class=" w-8 text-xs font-normal block">copied</span>
                        </span>
                    `;
                    copyBtn.addEventListener('click', async event => {
                        event.stopPropagation();
                        await withVisibleElements(async () => {
                            let htmlToCopy = (this.copyMode === 'html' && htmlCopySupported)
                                ? getFullHTML(headerEl)
                                : getCleanHTML(headerEl);
                            let textToCopy = getCleanText(headerEl) + '\n';
                            [...this.$el.children].forEach(child => {
                                const childLevel = child.dataset.level;
                                if (childLevel && childLevel.startsWith(headerId + '.')) {
                                    htmlToCopy += (this.copyMode === 'html' && htmlCopySupported)
                                        ? getFullHTML(child)
                                        : getCleanHTML(child);
                                    textToCopy += getCleanText(child) + '\n';
                                }
                            });
                            if (event.ctrlKey || event.metaKey || this.copyMode === 'text') {
                                if (window.TurndownService) {
                                    const turndownService = new TurndownService({ headingStyle: 'atx' });
                                    if (window.turndownPluginGfmTables) {
                                        turndownService.use(window.turndownPluginGfmTables);
                                    }
                                    navigator.clipboard.writeText(turndownService.turndown(htmlToCopy).trim());
                                } else {
                                    navigator.clipboard.writeText(textToCopy.trim());
                                }
                            } else {
                                const blobInput = new Blob([htmlToCopy], { type: 'text/html' });
                                const clipboardItem = new ClipboardItem({ 'text/html': blobInput });
                                navigator.clipboard.write([clipboardItem]);
                            }
                        });
                        const notCopied = copyBtn.querySelector('.copy-not-copied');
                        const copiedSpan = copyBtn.querySelector('.copy-all-copied') || copyBtn.querySelector('.copy-copied');
                        if (notCopied && copiedSpan) {
                            notCopied.style.display = 'none';
                            copiedSpan.style.display = 'flex';
                            setTimeout(() => {
                                notCopied.style.display = 'flex';
                                copiedSpan.style.display = 'none';
                            }, 2000);
                        }
                    });
                    headerEl.appendChild(copyBtn);
                }
            } else {
                // Associate non-header elements with the last header.
                el.dataset.level = lastHeader ? lastHeader + '.content' : '';
            }
        });
            this.updateVisibility();
            // this.updateToggleAllBtnText();
    },
    updateVisibility() {
        [...this.$el.children].forEach(el => {
            if (el.dataset.ignore) return;
            const lvl = el.dataset.level;
            let shouldHide = false;
            for (let key in this.collapsed) {
                if (this.collapsed[key] && lvl.startsWith(key + '.')) {
                    shouldHide = true;
                    break;
                }
            }
            if (shouldHide) {
                el.style.maxHeight = 0;
                el.style.overflow = 'hidden';
                el.style.opacity = 0;
                el.classList.add("!my-0");
            } else {
                el.style.maxHeight = el.scrollHeight + 'px';
                el.style.opacity = 1;
                el.classList.remove("!my-0");
            }
            // --- NEW: Update header toggle button icon ---
            if (/^H[1-6]$/.test(el.tagName)) {
                const headerToggleBtn = el.querySelector('[data-header-toggle]');
                if (headerToggleBtn) {
                    if (!this.collapsed[el.dataset.level]) {
                        headerToggleBtn.querySelector('.toggle-header-closed').style.display = 'none';
                        headerToggleBtn.querySelector('.toggle-header-opened').style.display = 'block';
                    } else {
                        headerToggleBtn.querySelector('.toggle-header-closed').style.display = 'block';
                        headerToggleBtn.querySelector('.toggle-header-opened').style.display = 'none';
                    }
                }
            }
            // --- END NEW ---
        });
    },
    toggleAll() {
        const allCollapsed = Object.values(this.collapsed).every(val => val);
        for (let key in this.collapsed) {
            this.collapsed[key] = !allCollapsed;
        }
        this.updateVisibility();
    },
    updateToggleAllBtnText() {
        const allCollapsed = Object.values(this.collapsed).every(val => val);
        const closed = this.toggleAllBtn.querySelector('.toggle-all-closed');
        const open = this.toggleAllBtn.querySelector('.toggle-all-opened');
        closed.style.display = allCollapsed ? 'flex' : 'none';
        open.style.display = allCollapsed ? 'none' : 'flex';
    },
});
